import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useAbly } from "ably/react";
import SpaceNotificationCard from "./SpaceMessage/SpaceMessageNotificationCard";
import SpaceStyleMessageList from "./SpaceMessage/SpaceMessageStyleList";
import SpaceStyleMessageInput from "./SpaceMessage/SpaceMessageStyleInput";
import SpaceMessageLoadingState from "./SpaceMessage/SpaceMessageLoadingState";
import type { SpaceMessage as SpaceMessageType, SpaceNotification } from "types/spaceTypes";
import { defaultSynthMember } from "./constants/defaultSynthMember";
import { v4 as uuidv4 } from "uuid";
import pako from "pako";
import useGetSpaceMessages from "hooks/spaceHooks/useGetSpaceMessages";
import useSendSpaceMessage from "hooks/spaceHooks/useSendSpaceMessage";
import usePinnedMessagesManager from "hooks/spaceHooks/usePinnedMessagesManager";
import useEditSpaceMessage from "hooks/spaceHooks/useEditSpaceMessage";
import useDeleteSpaceMessage from "hooks/spaceHooks/useDeleteSpaceMessage";
import useGetUserProfile from "hooks/useGetUserProfile";
import { useAppSelector } from "hooks/storeHooks";
import { getFromStorage } from "helpers";
import Paper from "components/common/Paper";
import Button from "components/common/Button";
import { useUnarchiveSpace } from "hooks/spaceHooks";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import config from "config/config";
import { authService } from "services/Ldb/authService";

type SpaceDetailsMessageTabProps = {
    onPinnedMessagesChange?: (pinnedMessages: SpaceMessageType[]) => void;
    spaceData?: any; // Space data containing members
};

export default function SpaceDetailsMessageTab({
    onPinnedMessagesChange,
    spaceData,
}: SpaceDetailsMessageTabProps) {
    const { spaceId, messageId } = useParams<{ spaceId: string; messageId?: string }>();

    // Get current user profile for real user data
    const [userProfile] = useGetUserProfile();
    const { fetchUserProfileSuccess } = useAppSelector((state) => state.auth);

    // Trigger profile fetch if needed
    useEffect(() => {
        if (!fetchUserProfileSuccess && !userProfile) {
        }
    }, [fetchUserProfileSuccess, userProfile]);

    // State for loading indicator
    const [isLoading, setIsLoading] = useState(false);

    // Real API integration for space messages - Only fetch initially, Ably handles real-time updates
    const getSpaceMessagesQuery = useGetSpaceMessages(spaceId || "", {
        pollingInterval: 0, // Disabled polling since Ably handles real-time updates
        refetchOnMount: true,
    });

    const { loadMoreMessages, hasMoreMessages, isLoadingMore } = getSpaceMessagesQuery;
    

    const [sendSpaceMessage, sendSpaceMessageResult] = useSendSpaceMessage();
    const [editSpaceMessage, editSpaceMessageResult] = useEditSpaceMessage();
    const [deleteSpaceMessage, deleteSpaceMessageResult] = useDeleteSpaceMessage();

    // const handlePinnedMessagesChange = useCallback((newPinnedMessages: SpaceMessageType[]) => {

    // Ably real-time listener for AI message responses using direct client
    const ably = useAbly();

    // Get real current user data (memoized to prevent infinite re-renders)
    const currentUserData = useMemo(() => {
        // Check ALL localStorage keys to find user data
        const storageKeys = [
            "ally-user",
            "user",
            "ldb-user",
            "profile",
            "guest-user",
            "space-guest",
            "guest",
            "current-user",
        ];
        let userData = null;

        for (const key of storageKeys) {
            try {
                const data = getFromStorage(key);
                if (data && typeof data === "object" && Object.keys(data).length > 0) {
                    if (!userData) userData = data; // Use first valid data found
                }
            } catch (error) {
                // Silent error handling
            }
        }

        // Also check the profile hooks data
        let currentUser = null;
        if (
            fetchUserProfileSuccess &&
            typeof fetchUserProfileSuccess === "object" &&
            Object.keys(fetchUserProfileSuccess).length > 0
        ) {
            currentUser = fetchUserProfileSuccess;
        } else if (
            userProfile &&
            typeof userProfile === "object" &&
            Object.keys(userProfile).length > 0
        ) {
            currentUser = userProfile;
        }

        const finalUser = currentUser || userData;

        // Determine user ID and info based on user type
        let userId = "unknown-user";
        let userName = "Unknown User";
        let userRole = "member";

        if (finalUser && typeof finalUser === "object") {
            // Look for guest indicators
            const isGuest =
                finalUser.isGuest ||
                finalUser.guestId ||
                finalUser.type === "guest" ||
                finalUser.role === "guest" ||
                finalUser.guest ||
                finalUser.user?.isGuest ||
                finalUser.user?.guestId ||
                finalUser.user?.type === "guest" ||
                finalUser.user?.role === "guest";

            if (isGuest) {
                // For guests, the ID is typically in user._id (from your data structure)
                userId =
                    finalUser.user?._id ||
                    finalUser.guestId ||
                    finalUser.user?.guestId ||
                    finalUser.guest?.guestId ||
                    finalUser._id ||
                    finalUser.id ||
                    finalUser.guest?._id ||
                    finalUser.guest?.id;

                userRole = "guest";

                // Try to get guest name (from space.members guest data)
                const guestMember = finalUser.space?.members?.find(
                    (m) => m.type === "guest" && m.guestId === userId
                );

                if (guestMember) {
                    userName =
                        `${guestMember.firstName || ""} ${guestMember.lastName || ""}`.trim() ||
                        `${guestMember.guest?.firstName || ""} ${
                            guestMember.guest?.lastName || ""
                        }`.trim() ||
                        "Guest User";
                    } else {
                    // Fallback to direct properties
                    userName =
                        finalUser.firstName && finalUser.lastName
                            ? `${finalUser.firstName} ${finalUser.lastName}`.trim()
                            : finalUser.firstName ||
                              finalUser.lastName ||
                              finalUser.name ||
                              finalUser.user?.firstName ||
                              finalUser.user?.lastName ||
                              finalUser.user?.name ||
                              finalUser.email ||
                              finalUser.user?.email ||
                              "Guest User";
                }
            } else {
                // Brand member
                userId = finalUser._id || finalUser.ldbId || finalUser.id || finalUser.user?._id;
                userRole = finalUser.role || finalUser.user?.role || "member";
                userName =
                    finalUser.fullName ||
                    finalUser.firstName ||
                    finalUser.name ||
                    finalUser.user?.fullName ||
                    "User";
            }
        }

        return {
            user: finalUser,
            id: userId,
            name: userName,
            role: userRole,
        };
    }, [fetchUserProfileSuccess, userProfile]);

    const currentUserId = currentUserData.id;
    const currentUserName = currentUserData.name;
    const currentUserRole = currentUserData.role;

    useEffect(() => {
        if (!spaceId || !ably) return;

        // Listen for Ably connection state changes to refetch on reconnect
        const handleConnectionStateChange = (stateChange: any) => {
            if (stateChange.current === "connected" && stateChange.previous === "disconnected") {
                getSpaceMessagesQuery.refetch();
            }
        };

        ably.connection.on("connected", handleConnectionStateChange);
        ably.connection.on("disconnected", handleConnectionStateChange);

        return () => {
            ably.connection.off("connected", handleConnectionStateChange);
            ably.connection.off("disconnected", handleConnectionStateChange);
        };
    }, [spaceId, ably, getSpaceMessagesQuery.refetch]);

    useEffect(() => {
        if (!spaceId || !ably) return;

        // Subscribe to the original general space channel
        const generalChannelName = `space:chat:${spaceId}`;
        const generalChannel = ably.channels.get(generalChannelName);

        // Subscribe to the new member-specific channel (only if we have a valid user ID)
        let memberChannel = null;
        let memberChannelName = null;
        if (currentUserId && currentUserId !== "unknown-user") {
            memberChannelName = `space:${spaceId}:${currentUserId}`;
            memberChannel = ably.channels.get(memberChannelName);
        }

        // Request notification permission on first load
        const requestNotificationPermission = async () => {
            if ("Notification" in window && Notification.permission === "default") {
                await Notification.requestPermission();
            }
        };
        requestNotificationPermission();

        // Shared message handler for both channels
        const handleMessage = async (message: any) => {
            // Check if data is compressed (Buffer format)
            let processedData = message?.data;
            if (
                message?.data?.data?.type === "Buffer" &&
                Array.isArray(message?.data?.data?.data)
            ) {
                try {
                    // Convert Buffer array back to Uint8Array
                    const bufferData = new Uint8Array(message.data.data.data);

                    // Check if it's gzipped (starts with gzip magic bytes: 31, 139)
                    if (bufferData[0] === 31 && bufferData[1] === 139) {
                        // Use pako.inflate - same as Workforce IQ
                        const decompressedText = pako.inflate(bufferData, { to: "string" });

                        // Parse the decompressed JSON
                        processedData = JSON.parse(decompressedText);
                    } else {
                        // Try to convert buffer to string
                        const textDecoder = new TextDecoder();
                        const decodedText = textDecoder.decode(bufferData);
                        processedData = JSON.parse(decodedText);
                    }
                } catch (decompressError) {
                    return; // Skip processing this message
                }
            }

            // Check if this matches our expected topic patterns: `updated:${spaceId}:${messageId}` or `created:${spaceId}:${messageId}`
            const updatePattern = new RegExp(`^updated:${spaceId}:`);
            const createPattern = new RegExp(`^created:${spaceId}:`);
            const isMessageUpdate = message?.name && updatePattern.test(message.name);
            const isMessageCreate = message?.name && createPattern.test(message.name);
            const isRelevantMessage = isMessageUpdate || isMessageCreate;

            if (isRelevantMessage && processedData) {
                
                // Show browser notification for messages from other users
                const isOwnMessage =
                    processedData.authorType !== "synth" &&
                    (processedData.author?.ldbId === currentUserId ||
                        processedData.author?.guestId === currentUserId ||
                        processedData.author?.ldb === currentUserId);

                if (
                    !isOwnMessage &&
                    "Notification" in window &&
                    Notification.permission === "granted"
                ) {
                    // Get author name with better fallback logic
                    let authorName = "Someone";
                    // Handle Synth messages specifically
                    if (processedData.authorType === "synth") {
                        authorName = "Synth";
                    } else if (processedData.author) {
                        const author = processedData.author;
                        // Try different name combinations
                        if (author.name) {
                            authorName = author.name;
                        } else if (author.fullName) {
                            authorName = author.fullName;
                        } else if (author.firstName && author.lastName) {
                            authorName = `${author.firstName} ${author.lastName}`.trim();
                        } else if (author.firstName) {
                            authorName = author.firstName;
                        } else if (author.lastName) {
                            authorName = author.lastName;
                        } else if (author.email) {
                            // Extract name from email if no other name is available
                            authorName = author.email.split("@")[0];
                        }

                        // For guest users, check guest object
                        if (author.guest) {
                            const guest = author.guest;
                            if (guest.firstName && guest.lastName) {
                                authorName = `${guest.firstName} ${guest.lastName}`.trim();
                            } else if (guest.firstName) {
                                authorName = guest.firstName;
                            } else if (guest.name) {
                                authorName = guest.name;
                            }
                        }

                        // Check spaceData.members for more complete name info
                        if (spaceData?.members && (authorName === "Someone" || !authorName)) {
                            const member = spaceData.members.find(
                                (m) =>
                                    m.ldbId === author.ldbId ||
                                    m.ldb === author.ldb ||
                                    m.guestId === author.guestId ||
                                    m.guest?._id === author.guestId
                            );

                            if (member) {
                                if (member.firstName && member.lastName) {
                                    authorName = `${member.firstName} ${member.lastName}`.trim();
                                } else if (member.firstName) {
                                    authorName = member.firstName;
                                } else if (member.guest?.firstName && member.guest?.lastName) {
                                    authorName =
                                        `${member.guest.firstName} ${member.guest.lastName}`.trim();
                                } else if (member.guest?.firstName) {
                                    authorName = member.guest.firstName;
                                } else if (member.guest?.email) {
                                    authorName = member.guest.email;
                                }
                            }
                        }

                        if (authorName === "Someone" && author.email) {
                            authorName = author.email;
                        }
                    }
                    // Show notification
                    new Notification(`New message from ${authorName}`, {
                        body: processedData.message?.substring(0, 100) || "New message in Space",
                        icon: "https://storage.googleapis.com/heysynth_files/synth_webapp/synthAllylogo_fhimzk.png",
                        tag: `space-message-${processedData._id}`, // Prevent duplicate notifications
                    });
                }

                // Refetch to get the latest messages with proper pagination
                try {
                    await getSpaceMessagesQuery.refetch();
                } catch (refreshError) {
                    // Silent error handling
                }
            }
        };

        // Subscribe to both channels
        generalChannel.subscribe(handleMessage);
        if (memberChannel) {
            memberChannel.subscribe(handleMessage);
        }

        // Cleanup function
        return () => {
            generalChannel.unsubscribe(handleMessage);
            if (memberChannel) {
                memberChannel.unsubscribe(handleMessage);
            }
        };
    }, [spaceId, ably, getSpaceMessagesQuery.refetch, currentUserId]);

    // Use API messages with proper pagination
    const rawApiMessages = getSpaceMessagesQuery.data || [];

    // Transform API messages to display format

    const apiMessages = useMemo(() => {
        // Transform API messages to match our SpaceMessage type
        const transformApiMessage = (apiMessage: any): SpaceMessageType => {
            // Transform author from API format to SpaceAuthor format
            // For AI messages (authorType: "synth"), there's no author field
            let transformedAuthor;
            if (apiMessage.authorType === "synth") {
                transformedAuthor = {
                    id: "synth",
                    name: "Synth",
                    avatar: defaultSynthMember.image, // Use Synth logo from codebase
                    role: "ai",
                };
            } else {
                // Handle both team members and guest users
                if (apiMessage.author?.ldb) {
                    // Team member
                    transformedAuthor = {
                        id: apiMessage.author.ldbId || apiMessage.author.ldb._id || "unknown",
                        name:
                            apiMessage.author.ldb.fullName ||
                            apiMessage.author.ldb.firstName ||
                            "Unknown User",
                        avatar: apiMessage.author.ldb.profile?.avatar,
                        role: apiMessage.author.ldb.role || "member",
                    };
                } else if (
                    apiMessage.author?.guest ||
                    (apiMessage.author?.guestId && apiMessage.author?.type === "guest")
                ) {
                    // Guest user - API data is incomplete, so get name from Space members data
                    const guestId = apiMessage.author.guestId || apiMessage.author.guest?._id;

                    // Find the guest in the space members to get complete name data
                    let guestName = "Guest User";
                    if (guestId && spaceData?.members) {
                        const guestMember = spaceData.members.find(
                            (member) => member.type === "guest" && member.guestId === guestId
                        );

                        if (guestMember) {
                            // Use space member data which has complete name info
                            guestName =
                                `${guestMember.firstName || ""} ${
                                    guestMember.lastName || ""
                                }`.trim() ||
                                `${guestMember.guest?.firstName || ""} ${
                                    guestMember.guest?.lastName || ""
                                }`.trim() ||
                                guestMember.guest?.email ||
                                apiMessage.author.guest?.email ||
                                "Guest User";
                        }
                    }

                    // Fallback to API data if space member not found
                    if (guestName === "Guest User") {
                        if (apiMessage.author.guest) {
                            guestName =
                                `${apiMessage.author.guest.firstName || ""} ${
                                    apiMessage.author.guest.lastName || ""
                                }`.trim() ||
                                apiMessage.author.guest.email ||
                                "Guest User";
                        } else {
                            guestName =
                                `${apiMessage.author.firstName || ""} ${
                                    apiMessage.author.lastName || ""
                                }`.trim() ||
                                apiMessage.author.email ||
                                "Guest User";
                        }
                    }

                    transformedAuthor = {
                        id: guestId || "unknown-guest",
                        name: guestName,
                        avatar: undefined, // Guests don't have avatars
                        role: "guest",
                    };
                } else {
                    // Fallback for unknown user structure
                    transformedAuthor = {
                        id: apiMessage.author?.id || "unknown",
                        name: apiMessage.author?.name || apiMessage.author?.email || "Unknown User",
                        avatar: apiMessage.author?.avatar,
                        role: "member",
                    };
                }
            }

            // Handle pinned status: API uses pinnedAt timestamp to indicate pinned messages
            const pinnedAt = apiMessage.pinnedAt || null;
            const isPinned = !!pinnedAt; // Message is pinned if pinnedAt exists
            const pinnedBy =
                apiMessage.pinnedBy || apiMessage.pinned_by || apiMessage.pinnedByUser || null;

                // Validate timestamp format (keep this for debugging if needed)
            if (apiMessage.createdAt && !moment(apiMessage.createdAt).isValid()) {
                // console.warn('⚠️ Invalid timestamp detected:', {
                //     messageId: apiMessage._id || apiMessage.id,
                //     author: transformedAuthor.name,
                //     rawCreatedAt: apiMessage.createdAt
                // });
                }
                
                return {
                id: apiMessage._id || apiMessage.id,
                content: apiMessage.message || apiMessage.content,
                createdAt: apiMessage.createdAt,
                author: transformedAuthor,
                type: apiMessage.authorType === "synth" ? "ai" : apiMessage.type || "user",
                messageType: apiMessage.messageType || "text",
                isPinned: isPinned,
                pinnedBy: pinnedBy,
                pinnedAt: pinnedAt,
                mentions: apiMessage.mentions,
                attachments: apiMessage.attachments, // Include file attachments
                editedAt: apiMessage.editedAt, // Include editedAt timestamp for edited messages
                // Tool call support for AI responses
                toolName: apiMessage.result?.toolName,
                toolCalls: apiMessage.result?.toolCalls || apiMessage.toolCalls,
                resultMessage:
                    apiMessage.result?.toolResult || apiMessage.result || apiMessage.resultMessage,
            };
        };

        const transformedMessages = rawApiMessages.map(transformApiMessage);

        return transformedMessages;
    }, [rawApiMessages]);

    const [notifications, setNotifications] = useState<SpaceNotification[]>([]);
    const [showCalendar, setShowCalendar] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const baseTimestamp = useRef(new Date().toISOString()); // Stable timestamp reference
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const lastScrollTop = useRef<number>(0);

    // Generate welcome message for empty spaces
    const generateWelcomeMessage = useCallback(
        (spaceName: string, spaceCreatedAt?: string): SpaceMessageType => {
            return {
                id: "welcome-message",
                content: `Welcome to your ${spaceName} Space
This is where planning and collaboration happen, all in one place.
• Chat with your teammates in real time
• Mention @Synth to get AI insights, generate forecasts, or automate tasks
• Assign actions to team members or to Flow to keep things moving
Whether you're planning, problem-solving, or reviewing, your Space is built for smarter teamwork.`,
                createdAt: spaceCreatedAt || new Date().toISOString(),
                author: {
                    id: "synth",
                    name: "Synth",
                    avatar: defaultSynthMember.image,
                    role: "ai",
                },
                type: "ai",
                messageType: "welcome",
            };
        },
        []
    );

    // Process space messages for display
    const allMessages = useMemo(() => {
        const currentMessages = apiMessages;

        // Add welcome message for empty spaces
        let messagesWithWelcome = [...currentMessages];
        if (messagesWithWelcome.length === 0 && spaceData?.name) {
            const welcomeMessage = generateWelcomeMessage(spaceData.name, spaceData.createdAt);
            messagesWithWelcome = [welcomeMessage];
        }

        // Sort messages by createdAt timestamp in chronological order (oldest first)
        // Ensure consistent timestamp parsing by normalizing to UTC
        const sortedMessages = messagesWithWelcome.sort((a, b) => {
            const timeA = moment.utc(a.createdAt).valueOf(); // Use moment for consistent parsing
            const timeB = moment.utc(b.createdAt).valueOf();
            return timeA - timeB;
        });

        // Messages are now properly sorted chronologically

        return sortedMessages;
    }, [apiMessages, spaceData?.name, spaceData?.createdAt, generateWelcomeMessage]);

    // Memoize the refetch function to prevent infinite loops
    const handleMessagesRefetch = useCallback(() => {
        getSpaceMessagesQuery.refetch();
    }, [getSpaceMessagesQuery.refetch]);

    // Use custom hook to manage pinned messages
    const { pinnedMessages, handlePinMessage, handleUnpinMessage, isPinning, isUnpinning } =
        usePinnedMessagesManager({
            spaceId: spaceId || "",
            allMessages,
            onMessagesRefetch: handleMessagesRefetch,
        });

    // Handle infinite scroll for loading more messages
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;

            // Save current scroll position
            lastScrollTop.current = scrollTop;

            // Check if user scrolled to top (within 100px of top)
            const nearTop = scrollTop < 100;

            if (nearTop && hasMoreMessages && !isLoadingMore && !getSpaceMessagesQuery.isLoading) {
                loadMoreMessages();
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [hasMoreMessages, isLoadingMore, loadMoreMessages, getSpaceMessagesQuery.isLoading]);

    // Handle pinned messages updates for parent component
    useEffect(() => {
        if (onPinnedMessagesChange) {
            onPinnedMessagesChange(pinnedMessages);
        }
    }, [pinnedMessages, onPinnedMessagesChange]);

    const scrollToBottom = () => {
        // Safety check with fallback
        if (!messagesEndRef.current) {
            // Try to find scroll container directly
            const scrollContainers = document.querySelectorAll(".overflow-y-auto");
            const targetContainer = Array.from(scrollContainers).find(
                (container) =>
                    container.className.includes("flex-1") &&
                    container.className.includes("scrollbar-hidden")
            ) as HTMLElement;

            if (targetContainer) {
                targetContainer.scrollTo({
                    top: targetContainer.scrollHeight,
                    behavior: "smooth",
                });
            }
            return;
        }

        const scrollContainer = messagesEndRef.current.closest(".overflow-y-auto") as HTMLElement;
        if (scrollContainer) {
            scrollContainer.scrollTo({
                top: scrollContainer.scrollHeight,
                behavior: "smooth",
            });
        }
    };

    // Keep track of previous message count and loading state
    const prevMessageCountTabRef = useRef(allMessages.length);
    const wasLoadingMoreTabRef = useRef(false);
    
    // Track loading state changes
    useEffect(() => {
        if (isLoadingMore) {
            wasLoadingMoreTabRef.current = true;
        }
    }, [isLoadingMore]);
    
    // Auto-scroll when NEW messages are sent/received (not when loading older messages)
    useEffect(() => {
        if (allMessages.length === 0) {
            return;
        }

        const previousCount = prevMessageCountTabRef.current;
        const currentCount = allMessages.length;
        const isNewMessage = currentCount > previousCount;
        const wasLoadingMore = wasLoadingMoreTabRef.current;
        

        // Block auto-scroll if we just finished loading older messages
        if (wasLoadingMore && !isLoadingMore) {
            wasLoadingMoreTabRef.current = false;
            prevMessageCountTabRef.current = currentCount;
            return;
        }

        // Block auto-scroll if currently loading
        if (isLoadingMore) {
            prevMessageCountTabRef.current = currentCount;
            return;
        }

        // Only auto-scroll for genuinely new messages (not old messages from pagination)
        if (isNewMessage) {
            const scrollTimer = setTimeout(() => {
                scrollToBottom();
            }, 300);
            
            prevMessageCountTabRef.current = currentCount;
            return () => clearTimeout(scrollTimer);
        } else {
            prevMessageCountTabRef.current = currentCount;
        }
    }, [allMessages.length, isLoadingMore, getSpaceMessagesQuery.data?.length]); // Watch all relevant states

    // Auto-scroll to bottom when Space is first loaded (initial messages fetch)
    // DISABLED: This was causing auto-scroll when loading older messages
    // useEffect(() => {
    //     if (
    //         getSpaceMessagesQuery.isSuccess &&
    //         !getSpaceMessagesQuery.isLoading &&
    //         allMessages.length > 0
    //     ) {
    //         setTimeout(() => {
    //             scrollToBottom();
    //         }, 300);
    //     }
    // }, [getSpaceMessagesQuery.isSuccess, getSpaceMessagesQuery.isLoading, allMessages.length]);

    // Auto-scroll when switching to a different Space (spaceId changes)
    useEffect(() => {
        if (spaceId) {
            const scrollTimer = setTimeout(() => {
                scrollToBottom();
            }, 800);

            return () => clearTimeout(scrollTimer);
        }
    }, [spaceId]);

    // Additional auto-scroll when messages are loaded for a new Space
    // DISABLED: This was also causing auto-scroll when loading older messages
    // useEffect(() => {
    //     if (getSpaceMessagesQuery.isSuccess && allMessages.length > 0) {
    //         setTimeout(() => {
    //             scrollToBottom();
    //         }, 400);
    //     }
    // }, [getSpaceMessagesQuery.isSuccess, spaceId, allMessages.length]);

    const handleEditMessage = async (messageId: string, newContent: string) => {
        if (!spaceId) return;

        try {
            await editSpaceMessage(spaceId, messageId, { message: newContent } as any);

            // Refresh messages from API
            getSpaceMessagesQuery.refetch();

            toast.custom((t) => (
                <CustomToast t={t} message="Message edited successfully" type="success" />
            ));
        } catch (error) {
            toast.custom((t) => (
                <CustomToast t={t} message="Failed to edit message" type="error" />
            ));
        }
    };

    const handleDeleteMessage = async (messageId: string) => {
        if (!spaceId) return;

        try {
            await deleteSpaceMessage(spaceId, messageId);

            // Refresh messages from API
            getSpaceMessagesQuery.refetch();
            
            // Show success toast
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="The message has been removed successfully."
                    type="success"
                    title="Message deleted"
                />
            ));
        } catch (error) {
            // Show error toast
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="We couldn't delete this message. Please try again."
                    type="error"
                    title="Delete Failed"
                />
            ));
        }
    };

    const handleMarkAsRead = async (messageId: string) => {
        if (!spaceId || !currentUserId || currentUserId === "unknown-user") {
            return;
        }

        // Check if message is already marked as read locally
        const isCurrentlyRead = readMessagesRef.current.has(messageId);
        
        try {
            if (isCurrentlyRead) {
                // Mark as unread: Remove from read set and call different API
                readMessagesRef.current.delete(messageId);
                
                // TODO: Temporarily commented out - will re-enable later
                /*
                // TODO: API endpoint for marking as unread - using unread endpoint for now
                await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}/unread`, {
                    method: 'PUT'
                });
                */
                
                // Trigger unread count increase
                window.dispatchEvent(new CustomEvent('space-message-marked-unread', { 
                    detail: { spaceId, messageId } 
                }));
                
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Message marked as unread."
                        type="success"
                        title="Read status updated"
                    />
                ));
            } else {
                // Mark as read: Add to read set
                readMessagesRef.current.add(messageId);
                
                // TODO: Temporarily commented out - will re-enable later
                /*
                // Mark message as read using the API endpoint
                await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}/unread`, {
                    method: 'PUT'
                });
                */

                // Publish to Ably space-specific channel with chat:read event
                if (ably) {
                    try {
                        await ably.channels.get(`space:chat:${spaceId}`).publish('chat:read', {
                            spaceId,
                            messageId,
                            userId: currentUserId
                        });
                    } catch (ablyError) {
                        // console.error('Failed to publish to Ably:', ablyError);
                        // Continue anyway - API call still worked
                    }
                }

                // Trigger a space list refetch to update the unread count
                window.dispatchEvent(new CustomEvent('space-message-marked-read', { 
                    detail: { spaceId, messageId } 
                }));
                
                // Show success toast (UI feedback)
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Message marked as read."
                        type="success"
                        title="Read status updated"
                    />
                ));
            }

        } catch (error) {
            // console.error("Error updating message read status:", error);
            // Revert local state on error
            if (isCurrentlyRead) {
                readMessagesRef.current.add(messageId);
            } else {
                readMessagesRef.current.delete(messageId);
            }
            
            // Trigger a sync to get the real count from server
            window.dispatchEvent(new CustomEvent('space-sync-unread-count', { 
                detail: { spaceId } 
            }));
            
            // Show error toast
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="We couldn't update the message status. Please try again."
                    type="error"
                    title="Error"
                />
            ));
        }
    };

    // Track read messages to avoid duplicate API calls
    const readMessagesRef = useRef<Set<string>>(new Set());
    const pendingReadMessagesRef = useRef<Set<string>>(new Set());

    // Batch mark multiple messages as read (for auto-read behavior)
    const handleBatchMarkAsRead = async (messageIds: string[]) => {
        if (!spaceId || !currentUserId || currentUserId === "unknown-user" || messageIds.length === 0) {
            return;
        }

        // Filter out messages that are already read or pending
        const newMessageIds = messageIds.filter(id => 
            !readMessagesRef.current.has(id) && !pendingReadMessagesRef.current.has(id)
        );

        if (newMessageIds.length === 0) {
            return;
        }

        // Mark as pending to avoid duplicate calls
        newMessageIds.forEach(id => pendingReadMessagesRef.current.add(id));

        try {
            // TODO: Temporarily commented out - will re-enable later
            /*
            // For now, we'll call the single message endpoint for each message
            // TODO: Update when backend supports batch endpoint
            const promises = newMessageIds.map(messageId => 
                authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}/unread`, {
                    method: 'PUT'
                })
            );

            await Promise.allSettled(promises);
            */

            // Publish batch read event to Ably
            if (ably) {
                try {
                    await ably.channels.get(`space:chat:${spaceId}`).publish('chat:read', {
                        spaceId,
                        messageIds: newMessageIds, // Send as array for batch
                        userId: currentUserId
                    });
                } catch (ablyError) {
                    // console.error('Failed to publish batch read to Ably:', ablyError);
                }
            }

            // Mark as read and remove from pending
            newMessageIds.forEach(id => {
                readMessagesRef.current.add(id);
                pendingReadMessagesRef.current.delete(id);
            });

            // Trigger space list refetch to update unread count
            window.dispatchEvent(new CustomEvent('space-message-marked-read', { 
                detail: { spaceId, messageIds: newMessageIds } 
            }));

        } catch (error) {
            // console.error('Failed to batch mark messages as read:', error);
            // Remove from pending on error
            newMessageIds.forEach(id => pendingReadMessagesRef.current.delete(id));
        }
    };

    // Parse mentions from message content
    const parseMentionsFromContent = (content: string): any[] => {
        const mentions: any[] = [];
        const spaceMembers = spaceData?.members || [];

        // Parse @Synth mentions
        if (content.includes("@Synth")) {
            mentions.push({
                kind: "synth",
                user: {
                    ldbId: currentUserData.user?._id || currentUserData.user?.ldbId,
                },
            });
        }

        // Parse @user mentions (space members and guests)
        spaceMembers.forEach((member) => {
            let memberName = "";
            let memberId = "";

            // Handle both team members and guests with the same logic as the input component
            if ("ldb" in member && member.ldb) {
                // Team member - use email if no name is available
                memberName =
                    member.ldb?.fullName ||
                    member.ldb?.firstName ||
                    member.ldb?.email ||
                    member.ldb?.profile?.businessName ||
                    "";
                memberId = member.ldbId || member.ldb._id;
            } else if ("guest" in member && member.guest) {
                // Guest user - prioritize name, fallback to email
                memberName =
                    `${member.guest?.firstName || ""} ${member.guest?.lastName || ""}`.trim() ||
                    member.guest?.email ||
                    "";
                memberId = member.guestId || member.guest._id;
            }

            if (memberName && content.includes(`@${memberName}`)) {
                // Different structure for normal users vs guests
                if ("ldb" in member && member.ldb) {
                    // Normal user - use ldbId
                    mentions.push({
                        kind: "user",
                        user: {
                            ldbId: memberId,
                        },
                    });
                } else if ("guest" in member && member.guest) {
                    // Guest user - use guestId for proper task creation
                    mentions.push({
                        kind: "user",
                        user: {
                            guestId: memberId,
                        },
                    });
                }
            }
        });

        // Parse @file mentions (pattern: @file[fileId])
        const fileMatches = content.match(/@file\[([^\]]+)\]/g);
        if (fileMatches) {
            fileMatches.forEach((match) => {
                const fileId = match.match(/@file\[([^\]]+)\]/)?.[1];
                if (fileId) {
                    mentions.push({
                        kind: "file",
                        file: {
                            fileId: fileId,
                        },
                    });
                }
            });
        }

        // Parse @note mentions (pattern: @note[noteId])
        const noteMatches = content.match(/@note\[([^\]]+)\]/g);
        if (noteMatches) {
            noteMatches.forEach((match) => {
                const noteId = match.match(/@note\[([^\]]+)\]/)?.[1];
                if (noteId) {
                    mentions.push({
                        kind: "note",
                        note: {
                            noteId: noteId,
                        },
                    });
                }
            });
        }

        // Parse @entity mentions (pattern: @inventory[entityId] or @entity[type:id])
        const entityMatches = content.match(/@(inventory|entity)\[([^\]]+)\]/g);
        if (entityMatches) {
            entityMatches.forEach((match) => {
                const fullMatch = match.match(/@(inventory|entity)\[([^\]]+)\]/);
                if (fullMatch) {
                    const type = fullMatch[1];
                    const id = fullMatch[2];

                    // Handle @inventory[id] format
                    if (type === "inventory") {
                        mentions.push({
                            kind: "entity",
                            entity: {
                                entityType: "inventory",
                                entityId: id,
                            },
                        });
                    }
                    // Handle @entity[type:id] format
                    else if (type === "entity" && id.includes(":")) {
                        const [entityType, entityId] = id.split(":");
                        mentions.push({
                            kind: "entity",
                            entity: {
                                entityType: entityType,
                                entityId: entityId,
                            },
                        });
                    }
                }
            });
        }

        return mentions;
    };

    const handleSendMessage = async (
        content: string,
        mentions?: any[],
        attachments?: any[],
        productReferences?: any[]
    ) => {
        // Use provided mentions if available, otherwise parse from content
        // This prevents duplicate mention parsing when mentions are already provided by the input component
        const parsedMentions = mentions && mentions.length > 0 ? mentions : parseMentionsFromContent(content);

        // Debug log to check attachments
        if (attachments && attachments.length > 0) {
            // console.log('📎 Sending message with attachments:', attachments);
        }

        // @Synth mentions are handled as regular mentions in the API payload

        if (!spaceId) {
            return;
        }

        try {
            const payload: any = {
                message: content,
            };

            // Add attachments to payload if any exist
            if (attachments && attachments.length > 0) {
                payload.attachments = attachments;
            }

            // Combine mentions and product references into a single mentions array
            const allMentions = [...parsedMentions];

            // Add product references as special mention objects
            if (productReferences && productReferences.length > 0) {
                // Helper function to check if a string is a valid MongoDB ObjectId
                const isValidObjectId = (id: string) => {
                    return /^[0-9a-fA-F]{24}$/.test(id);
                };

                const productMentions = productReferences
                    .map((product) => {
                        // Only use actual MongoDB ObjectIds - check product.id and product._id
                        const candidateIds = [product.id, product._id].filter(Boolean);
                        const validEntityId = candidateIds.find((id) => isValidObjectId(id));

                        // Only create entity mention if we have a valid ObjectId
                        if (!validEntityId) {
                            return null;
        }
        
        return {
                            kind: "entity",
                            entity: {
                                entityType: "inventory", // Use 'inventory' as per backend example
                                entityId: validEntityId, // Only valid MongoDB ObjectIds
                                // Store additional product metadata within entity
                                sku: product.sku,
                                name: product.name,
                                fullDisplayName: product.fullDisplayName,
                                type: product.type,
                            },
                        };
                    })
                    .filter(Boolean); // Remove null entries

                if (productMentions.length > 0) {
                    allMentions.push(...productMentions);
                }
            }

            // Add all mentions to payload if any exist
            if (allMentions.length > 0) {
                payload.mentions = allMentions;
            }

            // Frontend confirmed to send only 1 @Synth mention - duplicate responses are backend-side

            // Send message via API
            const response = await sendSpaceMessage(spaceId, payload);

            // @Synth mentions are already handled in the mentions array sent to the Space API
            // No need for additional CopilotKit integration

            // Ably handles real-time updates, so no need to refetch
            // This prevents unnecessary loading states after sending messages
            // await getSpaceMessagesQuery.refetch();

            // Auto-scroll to the new message
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        } catch (error) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="Failed to send message. Please try again."
                    type="error"
                />
            ));
        }
    };

    const handleDismissNotification = (id: string) => {
        setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    };

    const handleJumpToDate = (option: string) => {
        const scrollContainer = messagesEndRef.current?.parentElement;
        if (!scrollContainer) return;
        
        // Helper function to find date header elements
        const findDateHeader = (targetDate: string) => {
            const dateHeaders = scrollContainer.querySelectorAll("[data-date-header]");
            return Array.from(dateHeaders).find(
                (header) => header.getAttribute("data-date-header") === targetDate
            );
        };
        
        switch (option) {
            case "today": {
                const today = moment().format("YYYY-MM-DD");
                const todayHeader = findDateHeader(today);
                if (todayHeader) {
                    todayHeader.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                break;
            }
            case "yesterday": {
                const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
                const yesterdayHeader = findDateHeader(yesterday);
                if (yesterdayHeader) {
                    yesterdayHeader.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                break;
            }
            case "last-week": {
                const lastWeek = moment().subtract(7, "days").format("YYYY-MM-DD");
                const lastWeekHeader = findDateHeader(lastWeek);
                if (lastWeekHeader) {
                    lastWeekHeader.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                break;
            }
            case "last-month": {
                const lastMonth = moment().subtract(1, "month").format("YYYY-MM-DD");
                const lastMonthHeader = findDateHeader(lastMonth);
                if (lastMonthHeader) {
                    lastMonthHeader.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                break;
            }
            case "beginning":
                // Scroll to the very beginning
                scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
                break;
            case "specific":
                // Show custom calendar modal
                setShowCalendar(true);
                break;
            default:
                break;
        }
    };

    const handleDateJump = (selectedDate: string | Date) => {
        const scrollContainer = messagesEndRef.current?.parentElement;
        if (!scrollContainer) return;

        // Format the selected date to match our date header format
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");

        // Find the date header for the selected date
        const dateHeaders = scrollContainer.querySelectorAll("[data-date-header]");
        const targetHeader = Array.from(dateHeaders).find(
            (header) => header.getAttribute("data-date-header") === formattedDate
        );

        if (targetHeader) {
            targetHeader.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // If the exact date is not found, find the closest date
            const availableDates = Array.from(dateHeaders)
                .map((header) => header.getAttribute("data-date-header"))
                .filter(Boolean)
                .sort();

            // Find the closest date (either before or after the selected date)
            const targetMoment = moment(selectedDate);
            let closestDate = availableDates[0];
            let smallestDiff = Math.abs(moment(closestDate).diff(targetMoment, "days"));

            for (const date of availableDates) {
                const diff = Math.abs(moment(date).diff(targetMoment, "days"));
                if (diff < smallestDiff) {
                    smallestDiff = diff;
                    closestDate = date;
                }
            }

            if (closestDate) {
                const closestHeader = Array.from(dateHeaders).find(
                    (header) => header.getAttribute("data-date-header") === closestDate
                );
                if (closestHeader) {
                    closestHeader.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        }
    };

    // Handle message permalink: scroll to and highlight specific message
    useEffect(() => {
        if (messageId && allMessages.length > 0) {
            const targetMessage = allMessages.find((msg) => msg.id === messageId);
            if (targetMessage) {
                // Wait for message list to render, then scroll to the message
                setTimeout(() => {
                    const messageElement = document.querySelector(
                        `[data-message-id="${messageId}"]`
                    );
                    if (messageElement) {
                        messageElement.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });

                        // Add highlight effect
                        messageElement.classList.add(
                            "animate-pulse",
                            "bg-yellow-100",
                            "ring-2",
                            "ring-yellow-300",
                            "rounded-lg"
                        );

                        // Remove highlight after 3 seconds
                        setTimeout(() => {
                            messageElement.classList.remove(
                                "animate-pulse",
                                "bg-yellow-100",
                                "ring-2",
                                "ring-yellow-300"
                            );
                        }, 3000);
                    }
                }, 500); // Give time for messages to render
            }
        }
    }, [messageId, allMessages.length]); // Only depend on length, not the entire array

    const [unarchiveSpaceMutation, unarchiveSpaceMutationResult] = useUnarchiveSpace();

    async function handleUnarchiveSpace() {
        try {
            await unarchiveSpaceMutation(spaceData?._id);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title="Space unarchived"
                    message="This space has been successfully restored and is now active again."
                    type="success"
                />
            ));
        } catch (error) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title="Failed to Unarchive"
                    message={
                        error?.message ||
                        "Something went wrong while restoring this space. Please try again."
                    }
                    type="error"
                />
            ));
        }
    }

    return (
        <div className="bg-white h-full p-[24px]">
            {/* Notifications */}
            {notifications.length > 0 && (
                <div className="space-y-2 mb-4">
                    {notifications.map((notification) => (
                        <SpaceNotificationCard
                            key={notification.id}
                            notification={notification}
                            onDismiss={handleDismissNotification}
                        />
                    ))}
                </div>
            )}

            {/* Chat container with sticky input */}
            <div className="flex flex-col min-h-full min-w-0">
                {/* Messages container */}
                <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hidden min-h-0 min-w-0"
                    style={{ maxHeight: "70vh", paddingBottom: "200px" }}
                >
                    {/* Loading indicator for pagination */}
                    {isLoadingMore && (
                        <div className="border-b border-gray-100">
                            <SpaceMessageLoadingState
                                isLoading={true}
                                message="Loading older messages..."
                                className="py-4"
                            />
                        </div>
                    )}

                    {getSpaceMessagesQuery.isLoading &&
                    allMessages.length === 0 &&
                    !getSpaceMessagesQuery.isSuccess ? (
                        <SpaceMessageLoadingState
                            isLoading={true}
                            message="Loading messages..."
                            className="h-32"
                        />
                    ) : currentUserId && currentUserId !== "unknown-user" ? (
                <SpaceStyleMessageList
                    messages={allMessages}
                    autoScroll={true}
                    onJumpToDate={handleJumpToDate}
                    onPinMessage={handlePinMessage}
                    onUnpinMessage={handleUnpinMessage}
                    onEditMessage={handleEditMessage}
                    onDeleteMessage={handleDeleteMessage}
                    onMarkAsRead={handleMarkAsRead}
                    onBatchMarkAsRead={handleBatchMarkAsRead}
                    readMessagesRef={readMessagesRef}
                    currentUserId={currentUserId}
                    spaceMembers={spaceData?.members || []}
                    loadMoreMessages={loadMoreMessages}
                    hasMoreMessages={hasMoreMessages}
                    isLoadingMore={isLoadingMore}
                />
                    ) : (
                        <SpaceMessageLoadingState
                            isLoading={true}
                            message="Loading user data..."
                            className="h-32"
                        />
                    )}
                    {/* Scroll target at the very end */}
                    <div ref={messagesEndRef} className="h-1 w-full"></div>
                </div>
            </div>
            
            {/* Sticky message input at bottom */}
            <div className="sticky bottom-6 bg-white border-gray-100 z-10">
                {spaceData?.isArchived ? (
                    <Paper className="flex items-start justify-between shadow-[0_8px_6px_0_rgba(212,212,212,0.04),0_10px_15px_-4px_rgba(212,212,212,0.10),0_4px_12px_-2px_rgba(212,212,212,0.03)]">
                        <div>
                            <p className="text-sm font-rocGroteskMedium text-g-75">
                                This space has been archived
                            </p>
                            <p className="text-xs font-rocGroteskMedium text-g-75 text-slate-500">
                                You're viewing an archived space. Content is read-only until it's
                                restored.
                            </p>
                        </div>

                        <Button
                            btnText="Unarchive Space"
                            btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px]"
                            onClick={() => handleUnarchiveSpace()}
                            isLoading={unarchiveSpaceMutationResult.isLoading}
                        />
                    </Paper>
                ) : (
                <SpaceStyleMessageInput
                    onSendMessage={handleSendMessage}
                    placeholder="Send a message"
                    spaceMembers={spaceData?.members || []}
                    currentUserId={currentUserId}
                />
                )}
            </div>

            {/* Custom Calendar Modal */}
            {showCalendar && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <h3 className="text-lg font-rocGroteskMedium text-g-75 mb-4">
                            Jump to a specific date
                        </h3>
                        <CustomCalendar
                            showDoubleView={false}
                            selectRange={false}
                            value={null}
                            maxDate={new Date()} // Prevent future dates
                            onChange={(selectedDate) => {
                                handleDateJump(selectedDate as Date);
                                setShowCalendar(false);
                            }}
                        />
                        <div className="flex justify-end mt-4">
                            <Button
                                btnText="Cancel"
                                btnClassname="!w-fit !py-2 !bg-gray-200 !text-gray-700"
                                onClick={() => setShowCalendar(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
